import { useState, useEffect, useRef, lazy, Suspense, useCallback } from 'react';
import Constants from '../../utilities/constants/Constants';
import levelsImg from '../../assets/images/levels.png';
import starImg from '../../assets/images/star.png';

const LevelLoader = ({ 
  activity, 
  selectedChapter, 
  student,
  onBack, 
  onComplete, 
  activityType,
  onBackHandlerChange,
  activitySourcePage
}) => {
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [levelComponents, setLevelComponents] = useState({});
  const [loading, setLoading] = useState(true);
  // Use the activity passed from props directly.
  // Previously we were refetching here, which was overriding customization filters.
  const resolvedActivity = activity;

  // Get activity configuration from Constants
  const activityConfig = Constants.ACTIVITY_CONFIG[activityType];

  // Dynamically import components based on config
  useEffect(() => {
    const loadComponents = async () => {
      // Get fresh config inside effect to avoid stale closure
      const config = Constants.ACTIVITY_CONFIG[activityType];
      
      if (!config) {
        setLoading(false);
        return;
      }

      try {
        const components = {};
        
        if (config.hasLevels && config.levels) {
          // Load all level components
          for (const level of config.levels) {
            const componentPath = level.component;
            const Component = lazy(() => import(`./${componentPath}.jsx`).then(module => ({ default: module.default })));
            components[level.id] = Component;
          }
        } else if (config.component) {
          // Single component activity
          const Component = lazy(() => import(`./${config.component}.jsx`).then(module => ({ default: module.default })));
          components[1] = Component;
        }
        
        setLevelComponents(components);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    loadComponents();
  }, [activityType]); // Only depend on activityType, not the config object

  // If no activity config found
  if (!activityConfig) {
    return (
      <div
        className="min-h-screen flex flex-col"
        style={{
          backgroundImage: `url(${levelsImg})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="text-center p-8 bg-white rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Activity Not Available</h2>
          <p className="mb-6">This activity type is not configured.</p>
          <button 
            onClick={onBack}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Chapter
          </button>
        </div>
      </div>
    );
  }

  // Get levels from config
  const levels = activityConfig.hasLevels ? activityConfig.levels : [];

  const handleLevelSelect = (level) => {
    setSelectedLevel(level);
  };

  const handleBackToLevels = (action, nextLevelId) => {
    if (action === 'nextLevel' && nextLevelId) {
      // Find and select the next level
      const nextLevel = levels.find(level => level.id === nextLevelId);
      if (nextLevel) {
        setSelectedLevel(nextLevel);
        return;
      }
    }

    // Special case for Practice Mode: 
    // If we're going back from Level 1 (the guide table), we should exit LevelLoader entirely
    // to avoid the auto-selection loop in Practice Mode.
    if (activitySourcePage === 'practice' && (!selectedLevel || selectedLevel.id === 1)) {
      onBackRef.current?.();
      return;
    }

    // Go back to level selection screen
    setSelectedLevel(null);
  };

  // Keep onBack stable via a ref to prevent infinite loops
  const onBackRef = useRef(onBack);
  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  const handleBackFromLevelLoader = useCallback(() => {
    // If we're inside a level, go back to level selection
    if (selectedLevel) {
      setSelectedLevel(null);
    } else {
      // If we're in level selection, go back to activities
      onBackRef.current?.();
    }
  }, [selectedLevel]);

  // Notify parent about back handler changes based on current state
  // Only depend on selectedLevel to avoid infinite loops
  // Note: We intentionally omit onBackHandlerChange and handleBackFromLevelLoader from deps
  // to prevent infinite loops, as they are handled via refs above
  const lastHandlerRef = useRef(null);
  useEffect(() => {
    // Special for practice mode: auto-select Level 1 for activities with multiple levels
    // to show the tabular guide/learning mode first.
    if (activitySourcePage === 'practice' && !selectedLevel && levels.length > 0) {
      const level1 = levels.find(l => l.id === 1);
      if (level1) {
        setSelectedLevel(level1);
      }
    }

    if (onBackHandlerChange && lastHandlerRef.current !== handleBackFromLevelLoader) {
      lastHandlerRef.current = handleBackFromLevelLoader;
      onBackHandlerChange(handleBackFromLevelLoader);
    }
  }, [selectedLevel, activityType, activitySourcePage, levels]);

  const handleActivityComplete = () => {
    setSelectedLevel(null);
    if (onComplete) {
      onComplete();
    }
  };

  // Loading state
  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: `url(${levelsImg})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading Activity...</p>
        </div>
      </div>
    );
  }

  // If a level is selected, render that level's component
  if (selectedLevel && levelComponents[selectedLevel.id]) {
    const LevelComponent = levelComponents[selectedLevel.id];
    
    // Determine if there's a next level
    const currentLevelIndex = levels.findIndex(level => level.id === selectedLevel.id);
    const hasNextLevel = currentLevelIndex >= 0 && currentLevelIndex < levels.length - 1;
    const nextLevelId = hasNextLevel ? levels[currentLevelIndex + 1].id : null;
    
    return (
      <Suspense
        fallback={
          <div
            className="min-h-screen flex items-center justify-center"
            style={{
              backgroundImage: `url(${levelsImg})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-gray-600 text-lg">Loading Level...</p>
            </div>
          </div>
        }
      >
        <LevelComponent
          activity={resolvedActivity}
          selectedChapter={selectedChapter}
          student={student}
          onBack={handleBackToLevels}
          onComplete={handleActivityComplete}
          currentLevelId={selectedLevel.id}
          nextLevelId={nextLevelId}
          hasNextLevel={hasNextLevel}
          activitySourcePage={activitySourcePage}
          {...(selectedLevel.props || {})}
        />
      </Suspense>
    );
  }

  // For single-level activities, render directly
  if (!activityConfig.hasLevels && levelComponents[1]) {
    const SingleComponent = levelComponents[1];
    return (
      <Suspense
        fallback={
          <div
            className="min-h-screen flex items-center justify-center"
            style={{
              backgroundImage: `url(${levelsImg})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-gray-600 text-lg">Loading Activity...</p>
            </div>
          </div>
        }
      >
        <SingleComponent
          activity={resolvedActivity}
          selectedChapter={selectedChapter}
          student={student}
          onBack={onBack}
          onComplete={onComplete}
          activitySourcePage={activitySourcePage}
        />
      </Suspense>
    );
  }

  // Render level selection screen with level images
  return (
    <div
      className="min-h-screen"
      style={{
        backgroundImage: `url(${levelsImg})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 min-h-screen flex flex-col justify-center">
        {/* Level Cards - horizontal row, centered in middle of screen */}
        <div className="flex flex-row justify-center items-center gap-8 overflow-x-auto pb-4">
          {levels.map((level) => {
            const fullTitle = level.title || `Level ${level.id}`;
            const [rawLine1, rawLine2] = fullTitle.split(':');
            const levelLabel = (rawLine1 || '').trim() || `Level ${level.id}`; // e.g. 'Level 1'
            const levelName = (rawLine2 || '').trim() || levelLabel; // e.g. 'Genders' or fallback

            return (
              <button
                key={level.id}
                type="button"
                onClick={() => handleLevelSelect(level)}
                className="group flex-shrink-0 flex flex-col items-center cursor-pointer focus:outline-none"
              >
                <div
                  className="relative w-48 h-40 mx-auto mb-2 bg-contain bg-center bg-no-repeat transition-transform duration-300 group-hover:-translate-y-2 group-hover:scale-105 flex items-center justify-center px-5"
                  style={{ backgroundImage: `url(${starImg})` }}
                >
                  <span className="max-w-[8rem] text-center text-sm sm:text-base font-extrabold tracking-wide text-amber-900 drop-shadow-[0_1px_1px_rgba(255,255,255,0.9)] break-words leading-snug">
                    {levelName}
                  </span>
                </div>
                <div className="text-center">
                  <h3 className="leading-tight mb-1 font-extrabold tracking-wide">
                    <span className="block text-sm sm:text-base text-amber-200 drop-shadow-[0_2px_2px_rgba(0,0,0,0.7)]">
                      {levelLabel}
                    </span>
                  </h3>
                </div>
              </button>
            );
          })}
        </div>

        {/* Back button */}
        <div className="mt-12 text-center">
          <button
            onClick={onBack}
            className="inline-flex items-center justify-center px-8 py-3 rounded-xl bg-gradient-to-b from-amber-400 via-amber-600 to-amber-800 text-sm font-extrabold text-amber-50 tracking-wide uppercase shadow-[0_4px_0_rgba(120,53,15,1),0_8px_16px_rgba(0,0,0,0.6)] border-2 border-amber-900/70 hover:brightness-110 transition-transform duration-200 hover:-translate-y-0.5"
          >
            ← Back to Chapter
          </button>
        </div>
      </div>
    </div>
  );
};

export default LevelLoader;
